/**
 * Turning "who started this run" into something an interface can print.
 *
 * Its own file because TWO readers ask the same question: the brain's list,
 * where several people's work sits side by side, and one company's card, where
 * the answer is usually "you" but must not be assumed. One rule in one place -
 * a second copy would drift the day somebody fixes a case in one of them.
 *
 * WHY THIS IS NOT JUST A TRANSLATION KEY. A name must never go through `t()`:
 * a colleague called "Auto" would come back out of the catalogue as the
 * autopilot. So the answer is a union - either a name to print exactly as it
 * stands, or a key the catalogue owns - and the caller keeps them apart.
 */
import type { BrainStarter } from "./siteBrain";

/** A name to print as it stands, a word the catalogue owns, or nothing to say. */
export type StarterLabel = { name: string } | { key: "you" | "auto" | "someone" } | null;

/** What one run carries about its starter. Structural, so both readers fit. */
export interface StarterSource {
  startedBy: BrainStarter;
  startedByName: string | null;
}

/**
 * @param run the row or card being drawn
 * @param meId the reader's own user id
 *
 * Null means SAY NOTHING. A run older than the column has no answer, and a dash
 * where a name goes reads as a person whose name we lost rather than as a
 * question nobody ever asked.
 */
export function starterLabel(run: StarterSource, meId: string): StarterLabel {
  if (run.startedBy.kind === "auto") return { key: "auto" };
  if (run.startedBy.kind !== "user") return null;
  // Checked before the name, so an operator reading their own work sees "you"
  // rather than their own name written out - the question is whose run it is,
  // and their name is the one answer they already know.
  if (run.startedBy.id === meId) return { key: "you" };
  // A name we could not resolve is a real person we cannot name - a deleted
  // account, or the other database not answering. "Somebody else" is true in
  // both cases; inventing a placeholder that reads like a name is not.
  return run.startedByName ? { name: run.startedByName } : { key: "someone" };
}
